import React, { FC, useEffect, useMemo } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import { graphql } from "@helpers/graphql";
import {
  ReviewsQuery,
  Tour,
  TourByIdWithVideoThumbnailQuery,
  ToursQuery,
} from "@graphql/types";
import { getNodes } from "@helpers/getNodes";
import { getProductType, searchURL } from "@helpers/searchURL";
import { useRouter } from "next/router";
import { captureTourEvent } from "@helpers/gtag/events/TourEvent";
import { useAuth } from "@helpers/hooks/useAuth";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import { TourStatus as Status } from "@graphql/types";
import { ShareButton } from "@components/ui/ShareButton";
import { SITE_URL } from "@constants";
import LoadingPage from "@components/pages/LoadingPage";
import { Fields, Sidebar } from "@components/pages/TourPage/Sidebar/Sidebar";
import Head from "@components/common/Head";
import ProductMicroFormat from "@components/common/MicroFormat/ProductMicroFormat";
import { TourStatus } from "@components/pages/TourPage/TourStatus";
import { Container } from "@components/ui/Container";
import { Breadcrumb } from "@components/ui/Breadcrumb";
import { Header } from "@components/pages/TourPage/Header";
import { Itinerary } from "@components/pages/TourPage/Itinerary";
import { RequestTour } from "@components/pages/TourPage/RequestTour";
import { Inclusions } from "@components/pages/TourPage/Inclusions";
import { Important } from "@components/pages/TourPage/Important";
import { CancelPolicy } from "@components/pages/TourPage/CancelPolicy";
import Reviews from "@components/common/Reviews";
import { SidebarMobile } from "@components/pages/TourPage/Sidebar/SidebarMobile";
import ToursBlock from "@components/common/ToursBlock";
import { ButtonLink } from "@components/ui/Button";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import {
  getReviewsVariables,
  loader_tourBySlug,
} from "@components/pages/TourPage/loader";
import { PreviewTag } from "@components/common/PreviewTag";
import useIsMobile from "@helpers/useIsMobile";
import { isInValidCountrySlug } from "@helpers/isInValidCountrySlug";
import { CityFooterData } from "@components/common/SEO/utils/getCityFooterData";
import { SEOCityLinks } from "@components/common/SEO/SEOCityLinksOld";
import BreadcrumbMicroFormat from "@components/common/MicroFormat/BreadcrumbMicroFormat";
import { ShowMore } from "@components/ui/ShowMore";
import { Paragraph } from "@components/ui/Paragraph";
import { Title } from "@components/ui/Title";
import { User } from "@components/pages/TourPage/User";
import GalleryNew, { IGalleryItemProps } from "@components/ui/GalleryNew";
import { t, Trans } from "@helpers/translate";
import { convertUrlParamsToQueryParam, ROUTES } from "@root/routes";

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = String(params?.id);
  const country = String(params?.country);

  if (isInValidCountrySlug(country) === false) {
    return { notFound: true };
  }
  const res = await loader_tourBySlug({ slug, country });

  //Check if tour name exists or its different from database

  if ("props" in res) {
    const tour = res["props"].initialData.tour;

    if (
      country &&
      slug &&
      tour.tourById?.url &&
      tour.tourById?.url !== `/${country}/tour/${slug}`
    ) {
      return {
        redirect: {
          destination: tour.tourById?.url,
          permanent: true,
        },
      };
    }
  }

  return res;
};

type Props = {
  initialData?: {
    tour: TourByIdWithVideoThumbnailQuery;
    supplierTours: ToursQuery;
    popularTours: ToursQuery;
    reviews: ReviewsQuery;
    tourPrice: number;
    footer: CityFooterData;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  const router = useRouter();
  const { auth } = useAuth();

  const isMobile = useIsMobile();

  const tour = initialData?.tour?.tourById;
  if (!tour) {
    return <LoadingPage />;
  }

  useEffect(() => {
    if (tour) {
      graphql.incrementTourViews({ id: tour.id });
      captureTourEvent(tour as Tour, auth?.id || 0);
    }
  }, [tour]);

  const galleryItems = useMemo(() => {
    const mdSize = [] as IGalleryItemProps[];
    const originalSize = [] as IGalleryItemProps[];

    if (tour.video) {
      mdSize.push({
        type: "video",
        src: tour.video,
        caption: undefined,
        thumbnail: tour.videoThumbnail || tour.picture?.url || "",
      });
      originalSize.push({
        type: "video",
        src: tour.video,
        caption: undefined,
        thumbnail: tour.videoThumbnail || tour.picture?.url || "",
      });
    }

    tour.gallery?.forEach((item) => {
      if (item.src) {
        mdSize.push({
          type: "image",
          src: item.src,
          alt: `${tour.city?.name} Private Tour - ${item.caption}`,
          caption: item.caption || "",
        });
      }
    });
    tour.galleryOriginal?.forEach((item) => {
      if (item.src) {
        originalSize.push({
          type: "image",
          src: item.src,
          alt: `${tour.city?.name} Private Tour - ${item.caption}`,
          caption: item.caption || "",
        });
      }
    });
    return {
      md: mdSize,
      original: originalSize,
    };
  }, [tour]);

  const productImages = useMemo(() => {
    const images: string[] = [];

    // Take cover picture
    if (tour.picture) {
      images.push(tour.picture?.serpImage || tour.picture?.url);
    }

    // Take gallery images
    tour.gallery?.forEach((galleryItem) => {
      images.push(galleryItem?.serpImage || galleryItem?.src);
    });
    return images;
  }, [tour]);

  const cityName = tour.city.name;
  const supplierName = tour.supplier.displayName;
  const tourTitle = tour.title;
  const countryName = tour?.country.name;
  const isCountryActive = tour?.country?.isActive;
  const categoryName = tour.category?.name;

  function onCheckout(input: Fields) {
    const searchParams = new URLSearchParams();
    searchParams.append("meeting_date", input.date?.toISOString() as string);
    searchParams.append("time", input.time as string);
    searchParams.append("adults", `${input.people.adults}`);
    searchParams.append("days_to_cancel", `${input.days_to_cancel}`);
    input.people.children &&
      searchParams.append("children", `${input.people.children}`);
    input.people.infants &&
      searchParams.append("infants", `${input.people.infants}`);

    if (!auth) {
      const url = ROUTES.country(tour!.country.slug)
        .tour(`${tour?.id}`)
        .checkout.signup(convertUrlParamsToQueryParam(searchParams));
      router.push(url, url);
      return;
    }

    const url = ROUTES.country(tour!.country.slug)
      .tour(`${tour?.id}`)
      .checkout.form(convertUrlParamsToQueryParam(searchParams));

    router.push(url, url);
  }

  const breadCrumbSteps = useMemo(() => {
    const steps = [];
    const toursText = "Tours";

    if (tour) {
      /* show category in bread crumb */
      if (tour.category) {
        steps.push({ text: tour.country.name, href: tour.country.url });
        steps.push({ text: tour.city.name, href: tour.city.url });
        steps.push({ text: toursText, href: `${tour.city.url}/tours` });
        steps.push({
          text: tour.category.name || "",
          href: `${tour.city.url}/category/${tour.category.slug}`,
        });
        steps.push({ text: tour.title, href: tour.url });

        return steps;
      }

      steps.push({ text: tour.country.name, href: tour.country.url });
      steps.push({ text: tour.city.name, href: tour.city.url });
      steps.push({ text: toursText, href: `${tour.city.url}/tours` });
      steps.push({ text: tour.title, href: tour.url });

      return steps;
    }
    return [];
  }, []);

  const noIndexFollow =
    !isCountryActive ||
    !(tour.isActive && tour.status === Status.Published) ||
    countryName === "Togo"
      ? true
      : false;

  const isPreviewPage = router.pathname.endsWith("/preview");
  const isActive = tour.isActive;
  const isPublished = tour.status === Status.Published;

  return (
    <>
      <Head
        title={t({
          id: "tour.seo.title",
          message: `${tourTitle} - ${cityName}`,
        })}
        canonical={tour.url}
        description={getMetaDataDescription(tour.overview || "")}
        openGraph={{
          images: [{ url: tour?.picture?.cover || "" }],
        }}
        noindex={noIndexFollow}
        nofollow={noIndexFollow}
      />

      {isPreviewPage &&
        tour &&
        (isActive == false || isPublished === false) && <PreviewTag noMargin />}

      <ProductMicroFormat
        productID={`${tour.city?.slug}_city_tours`}
        name={`${cityName} Guided Tours`}
        description={t({
          id: "city.tours.seo.description",
          message: `Top ${cityName}, ${countryName} Private Tours and things to do: Visit ${cityName}, ${cityName} with Local Guides. Check reviews, photos, request an itinerary, plan a day trip or walking tour.`,
        })}
        image={productImages}
        reviewCount={tour.supplier?.stats.reviewsCount}
        offerPrice={initialData.tourPrice}
      />

      <TourStatus status={tour.status} />

      <Container className="pt-2">
        <div className="flex flex-row relative">
          <div className="flex w-9/12 md:w-10/12">
            <div className="flex overflow-x-auto w-full items-center">
              <Breadcrumb items={breadCrumbSteps} noWrap />
            </div>
            <BreadcrumbMicroFormat items={breadCrumbSteps} />
          </div>
          <div className="flex ml-auto">
            <ShareButton
              type="tour"
              country={countryName}
              city={cityName}
              title={tour.title}
              url={SITE_URL + tour.url}
            />
          </div>
        </div>

        {tour.gallery && (
          <div className="mt-2">
            <GalleryNew gallery={galleryItems} />
          </div>
        )}

        <div className="flex flex-wrap mt-6 mb-20">
          <div className="w-full lg:w-2/3 lg:pr-8">
            <div>
              <Header tour={tour} />

              <ShowMore
                className="mt-6 mb-20"
                height={700}
                moreText={`${t({
                  id: "viewMore.button.more.generic",
                  message: "View More",
                })}`}
                lessText={`${t({
                  id: "viewLess.button.more.generic",
                  message: "View Less",
                })}`}
              >
                <Itinerary
                  itinerary={tour.itinerary}
                  showTitle={true}
                  showSubTitle={true}
                  className="mb-0"
                />
              </ShowMore>

              <RequestTour
                supplier={tour.supplier}
                tourId={tour.id}
                tour={tour}
              />

              <Inclusions tour={tour} />
              <Important tour={tour} />
              <CancelPolicy rules={tour.cancelPolicyRules} />

              <User
                displayName={tour.supplier.displayName}
                rating={tour.supplier.stats.rating}
                reviewsCount={tour.supplier.stats.reviewsCount}
                picture={
                  isMobile
                    ? tour.supplier?.picture?.urlXS
                    : tour.supplier.picture?.url
                }
                cityName={tour.city.name}
                createdAt={tour.supplier.createdAt}
                url={tour.supplier.url}
                onReviewClicked={() => {
                  router.push("#reviews");
                }}
                onUserClicked={() => {
                  router.push(tour.supplier.url);
                }}
                showVerifiedData
              />

              <ShowMore className="mt-6 mb-20">
                <Title as="h1" size="md" divider>
                  {tour.supplier.isCar &&
                    t({
                      id: "supplier.car.block.about.title",
                      message: `About ${supplierName}`,
                    })}

                  {!tour.supplier.isCar &&
                    t({
                      id: "supplier.block.about.title",
                      message: `About Tour Guide ${supplierName}`,
                    })}
                </Title>

                <Paragraph>{tour.supplier.biography}</Paragraph>
              </ShowMore>

              <div id="reviews">
                <Reviews
                  type="tours"
                  initialData={initialData?.reviews}
                  filters={getReviewsVariables(tour.supplier.id)}
                  title={t({
                    id: "city.block.review-title",
                    message: "Reviews",
                  })}
                />
              </div>
            </div>
          </div>
          <div className="w-1/3">
            <Sidebar tour={tour} onCheckOut={onCheckout} isModal={false} />
            <SidebarMobile tour={tour} onCheckOut={onCheckout} />
          </div>
        </div>

        <div className="w-full">
          <div className="mobile-negative-margin">
            {/* Supplier Tours */}
            {initialData?.supplierTours.tours.totalCount > 0 && (
              <ToursBlock
                title={t({
                  id: "tour.block.supplier-tours.title",
                  message: `More tours by ${supplierName}`,
                })}
                columns="4"
                showUser={false}
                data={getNodes(initialData?.supplierTours.tours)}
                enableSwipeOnMobile={true}
              >
                <ButtonLink
                  variant="default-outline"
                  href={getAbsoluteUrl(tour.supplier.url)}
                  title={t({
                    id: "tours.block.supplier-tours.button.gowithguide",
                    message: "GoWithGuide - See all tours in guide",
                  })}
                >
                  <Trans id="tours.block.supplier-tours.button">
                    See all tours by {supplierName}
                  </Trans>
                </ButtonLink>
              </ToursBlock>
            )}

            {/* Popular Tours */}
            {initialData?.popularTours.tours.totalCount > 0 && (
              <ToursBlock
                title={t({
                  id: "tour.block.popular-tours.title",
                  message: `Other Popular ${categoryName} Tours in ${cityName}`,
                })}
                columns="4"
                data={getNodes(initialData?.popularTours.tours)}
                enableSwipeOnMobile={true}
              >
                <ButtonLink
                  className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
                  variant="default-outline"
                  target="_blank"
                  href={getAbsoluteUrl(
                    searchURL({
                      city: tour.city.slug,
                      category: tour.category?.slug,
                      t: getProductType(tour.productType),
                    })
                  )}
                  title={t({
                    id: "tours.block.popular-tours.button.gowithguide",
                    message: "GoWithGuide - See all tours in city",
                  })}
                >
                  <Trans id="tours.block.popular-tours.button">
                    See all {categoryName} tours in {cityName}
                  </Trans>
                </ButtonLink>
              </ToursBlock>
            )}
          </div>

          {initialData.tour.tourById && (
            <SEOCityLinks {...initialData.footer} />
          )}
        </div>
      </Container>
    </>
  );
};

export default Page;
